import { Play, Sparkles, Square } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { useCaseIde } from "../../context/CaseIdeContext";
import { workflowById } from "../catalog";
import { useWorkflowsOptional } from "../context/WorkflowsContext";
import { useOpenWorkflowsTab } from "../hooks/useOpenWorkflowsTab";
import { ProgressPips } from "./ProgressPips";
import { RunAllButton } from "./RunAllButton";
import { useRunLine } from "./WorkflowRunLine";

const StripBody: FC = () => {
  const w = useWorkflowsOptional();
  const tab = useOpenWorkflowsTab();
  const runLine = useRunLine();
  if (!w) return null;
  const next = w.nextUp ? workflowById(w.nextUp) : null;

  return (
    <div
      className="flex h-8 shrink-0 items-center gap-2 border-b border-line-1 bg-chrome px-3 text-sm"
      data-testid="workflow-strip"
    >
      <button
        type="button"
        onClick={tab.openPrepare}
        className="flex shrink-0 items-center gap-1.5 rounded-xs font-medium text-fg-2 outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <Sparkles
          className={cn(
            "size-3.5 text-accent-seed",
            w.running && "animate-pulse"
          )}
          aria-hidden
        />
        Case Prep
      </button>
      <ProgressPips views={w.views} className="shrink-0" />
      <span className="shrink-0 text-fg-3 tabular-nums">{runLine}</span>
      {!w.running && next ? (
        <span className="min-w-0 truncate text-fg-3">
          · Next: <span className="text-fg-2">{next.title}</span>
        </span>
      ) : null}
      <span className="flex-1" />
      {w.running ? (
        <Button variant="ghost" size="xs" onClick={w.stop}>
          <Square />
          Stop
        </Button>
      ) : w.remaining.length > 1 ? (
        <RunAllButton mode="remaining" size="xs" />
      ) : next ? (
        <Button
          size="xs"
          onClick={() => w.run(next.id)}
          data-testid="workflow-strip-run-next"
        >
          <Play />
          Run
        </Button>
      ) : null}
    </div>
  );
};

/**
 * The slim Case Prep strip above an open file or draft: how far the case
 * has come, what's next, and Run Remaining. The launchpad itself carries the
 * same, so the strip hides where it shows.
 */
export const WorkflowStrip: FC = () => {
  const { activeArtifact } = useCaseIde();
  const w = useWorkflowsOptional();
  if (!w || !activeArtifact || activeArtifact.kind === "workflows") return null;
  if (w.facts.files === 0) return null;
  return <StripBody />;
};
